import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/hooks/use-i18n";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle } from "lucide-react";

interface DisclaimerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DisclaimerDialog({ open, onOpenChange }: DisclaimerDialogProps) {
  const [accepted, setAccepted] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();

  const handleAccept = () => {
    localStorage.setItem("partyflow_disclaimer_accepted", "true");
    onOpenChange(false);
    navigate("/app?mode=signup");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5 text-[hsl(var(--color-yellow))]" />
            {t("disclaimerTitle")}
          </DialogTitle>
          <DialogDescription className="text-left pt-2 space-y-3 text-sm leading-relaxed">
            <p>{t("disclaimerIntro")}</p>
            <p>{t("disclaimerBeta")}</p>
            <p>{t("disclaimerNature")}</p>
            <p>{t("disclaimerLiability")}</p>
            <ul className="list-disc pl-5 space-y-1">
              {t("disclaimerLiabilityItems").split("|").map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p>{t("disclaimerRisk")}</p>
            <p>{t("disclaimerPrivacy")}</p>
            <p>{t("disclaimerLaw")}</p>
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-3 pt-2">
          <Checkbox
            id="accept-disclaimer"
            checked={accepted}
            onCheckedChange={(checked) => setAccepted(!!checked)}
          />
          <label htmlFor="accept-disclaimer" className="text-sm cursor-pointer leading-tight">
            {t("disclaimerAccept")}
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={handleAccept} disabled={!accepted}>{t("disclaimerAcceptBtn")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
